import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';

import { api } from '../../api/endpoints.js';
import { PERMISSIONS } from '../../lib/permissions.js';
import { setSession, getToken, getStoredAdmin, setUnauthorizedHandler } from '../../api/client.js';

/**
 * Admin session.
 *
 * Permissions come from the server on every sign-in and are re-read from
 * `/auth/me` on reload. The UI uses them to hide controls, but that is a
 * convenience only — the API checks the same permission on every request, so
 * a hidden button is not the security boundary.
 */

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [admin, setAdmin] = useState(() => getStoredAdmin());
  const [status, setStatus] = useState(() => (getToken() ? 'loading' : 'signed_out'));

  const signOut = useCallback(() => {
    setSession(null, null);
    setAdmin(null);
    setStatus('signed_out');
  }, []);

  useEffect(() => {
    setUnauthorizedHandler(signOut);
  }, [signOut]);

  useEffect(() => {
    if (!getToken()) return;

    // A stored profile can be stale — permissions may have been changed by
    // another admin — so it is always reconciled with the server.
    api.auth
      .me()
      .then((profile) => {
        setAdmin(profile);
        setSession(getToken(), profile);
        setStatus('signed_in');
      })
      .catch(() => signOut());
  }, [signOut]);

  const signIn = useCallback(async (credentials) => {
    const result = await api.auth.login(credentials);
    const profile = { ...result.admin, permissions: result.permissions };

    setSession(result.accessToken, profile);
    setAdmin(profile);
    setStatus('signed_in');

    return profile;
  }, []);

  const can = useCallback(
    (...permissions) => permissions.every((permission) => admin?.permissions?.includes(permission)),
    [admin],
  );

  /**
   * Any one of these is enough.
   *
   * For a screen several roles reach for different reasons — the credits
   * screen is a ledger to finance, a price list to operations — where gating
   * on the strictest permission would shut out everyone who has one of the
   * others.
   */
  const canAny = useCallback(
    (...permissions) => permissions.some((permission) => admin?.permissions?.includes(permission)),
    [admin],
  );

  const value = useMemo(
    () => ({ admin, status, signIn, signOut, can, canAny }),
    [admin, status, signIn, signOut, can, canAny],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error('useAuth must be used inside AuthProvider');
  return context;
}

export { PERMISSIONS };
